// MIDAS V3 — <CompRefV3>
//
// Render layer for the bot's diagram-component reference tag (DIAG-OVERLAY-B,
// bot ticket DIAG-COMPONENT-CHIPS-1):
//   Single sheet: <comp-ref component="SQ431" pages="1043">SQ431</comp-ref>
//   Multi  sheet: <comp-ref component="XB1:00" pages="1043,1049">XB1:00</comp-ref>
//
// Spec: IMPLEMENTATION_TEMPLATES/ToDo/MidasChat/DIAG_OVERLAY_PARTB_COMP_REF_CHIPS_SPEC.md
//
// Same affordance FAMILY as <LiveRefV3> / <TelRefV3> (mono face, pip +
// mini-label, hover tooltip, v3 CSS-variable tokens, no MUI), with one
// deliberate difference: this chip IS clickable. <plc-ref>/<live-ref> render a
// no-op placeholder click; a comp-ref click runs the §4 flash/blink algorithm.
// The accent-toned pip + "CMP" label carry that affordance (tel-ref's muted
// treatment reads as "citation only").
//
// CLICK WIRING — deliberately NOT an onClick prop.
// The §4 algorithm needs `message.artifacts` (rule (c) background-adds the
// artifact for a page that has no open tab), which the markdown renderer does
// not have. So the chip publishes its payload as data attributes and
// ChatBotMessageV3's message-level `onClickCapture` delegate executes the
// algorithm — the same DOM-delegation idiom CiteV3 uses via
// `button.midas-v3-cite[data-cite-page]`. Keyboard activation synthesises a
// real click so the delegate sees one code path.
//
// No MUI, no @emotion, no `sx`, no `dt` tokens.
// Plain inline styles + scoped CSS classes from ./comp-ref.css.
import * as React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { Tooltip, TooltipContent, TooltipTrigger } from "../v2/ui/tooltip";

export interface CompRefV3Props {
  /** Hotspot `component_id`, EXACTLY as served by
   *  `GET /api/v1/diagram/{page}/hotspots`. Opaque to the renderer. */
  component: string;
  /** Global overlay page numbers this component is drawn on. ALWAYS a list
   *  (single-page chips arrive as a one-element list — spec §1). */
  pages: number[];
  /** Verbatim inner-text chip face; falls back to `component` upstream. */
  label: string;
}

export const CompRefV3: React.FC<CompRefV3Props> = ({
  component,
  pages,
  label,
}) => {
  const { translate } = useTranslation("chatBot");

  // Locale-neutral page join for both the tooltip and the data attribute.
  // The data attribute MUST stay a plain comma list — ChatBotMessageV3 parses
  // it back with the same parseCompRefPages() the sentinel decoder uses.
  const pagesText = pages.join(", ");
  const pagesAttr = pages.join(",");
  const isMultiSheet = pages.length > 1;

  const componentLabel = translate("compRef.tooltip.component", {
    defaultValue: "Component:",
  }) as string;
  const sheetLabel = translate(
    isMultiSheet ? "compRef.tooltip.sheets" : "compRef.tooltip.sheet",
    { defaultValue: isMultiSheet ? "Sheets:" : "Sheet:" }
  ) as string;
  const ariaChip = translate("compRef.aria.chip", {
    defaultValue: "Diagram component {{component}} on sheet {{pages}}",
    component,
    pages: pagesText,
  }) as string;

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLSpanElement>) => {
      if (e.key !== "Enter" && e.key !== " ") return;
      e.preventDefault();
      // Synthesise a real click so the message-level onClickCapture delegate
      // handles keyboard and pointer activation through ONE path.
      e.currentTarget.click();
    },
    []
  );

  const chip = (
    <span
      className="comp-ref"
      role="button"
      tabIndex={0}
      aria-label={ariaChip}
      data-comp-id={component}
      data-comp-pages={pagesAttr}
      onKeyDown={handleKeyDown}
    >
      <span className="comp-ref__pip" aria-hidden />
      <span className="comp-ref__label">CMP</span>
      <span className="comp-ref__face">{label}</span>
      {/* Multi-sheet marker — a bare count, no picker. The owner ruling
          (spec §2) explicitly rules OUT a mini-picker popover; every sheet is
          acted on at once, so this is informational only. */}
      {isMultiSheet && (
        <span className="comp-ref__count" aria-hidden>
          ×{pages.length}
        </span>
      )}
    </span>
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>{chip}</TooltipTrigger>
      <TooltipContent
        side="top"
        style={{
          // Explicit opaque surface via V3 tokens — the shadcn `bg-popover`
          // token is undefined on .midas-v3-root and renders transparent.
          // See the long rationale in LiveRefV3.tsx / TelRefV3.tsx.
          background: "var(--card)",
          color: "var(--ink)",
          border: "1px solid var(--border)",
          boxShadow: "var(--shadow)",
          borderRadius: 6,
          padding: "8px 10px",
          fontFamily: "var(--font-sans)",
          fontSize: 12,
          lineHeight: 1.4,
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <div>
            <span style={{ color: "var(--muted)", marginRight: 6 }}>
              {componentLabel}
            </span>
            <span style={{ fontFamily: "var(--font-mono)", color: "var(--ink)" }}>
              {component}
            </span>
          </div>
          <div>
            <span style={{ color: "var(--muted)", marginRight: 6 }}>
              {sheetLabel}
            </span>
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontWeight: 600,
                color: "var(--ink)",
              }}
            >
              {pagesText}
            </span>
          </div>
        </div>
      </TooltipContent>
    </Tooltip>
  );
};

export default CompRefV3;
